import Loader from 'reusable/mobx-helpers/Loader'
import { makeAutoObservable } from 'mobx'
import { FormHandlerConfig, formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { AutocompleteOption } from 'reusable/mui/auto-complete/shared/AutocompleteOption'
import { monthNamesShort, notNull } from '@gap/utils/utils'
import ProposalsEditStore from '../ProposalsEditStore'
import { ProposalStep4SigneesEnum, ProposalStep4DueDateEnum, ProposalStep4TimingEnum } from '@gap/backend/app-services/proposal/models/ProposalStep4Dto'
import { apiClient } from 'api/trpc/trpc-client'
import { decodeFile } from 'reusable/utils/file-operations'
import { GapAutocompleteHierarchy, GapAutocompleteOption } from 'features/gap-components/autocomplete/types/GapAutocompleteTypes'
import DateUtils from '@gap/utils/date-utils'
import proposalObjectivesAndValues from '@gap/utils/proposals/proposalObjectivesAndValues'
import proposalExpectations from '@gap/utils/proposals/proposalExpectations'

interface ProposalsEditDetailsStoreProps {
   proposalsEditStore: ProposalsEditStore
}

interface FormFields {
   timing: ProposalStep4TimingEnum
   duringMonth: Date | null
   signees: ProposalStep4SigneesEnum
   signeesAmount: number
   dueDate: ProposalStep4DueDateEnum
   dueDateDate: Date | null
   selectedFilename: string | null
   objectivesAndValue: string[]
   expectations: string[]
}

export default class ProposalsEditDetailsStore {
   loader = new Loader(false)
   existingTOEFileLoader = new Loader(false)

   fields: FormFields = {
      timing: "upon-acceptance",
      duringMonth: null,
      signees: 'all-signees',
      signeesAmount: 1,
      dueDate: '2-weeks-from-now',
      dueDateDate: null,
      selectedFilename: null,
      objectivesAndValue: [],
      expectations: []
   }
   sp
   fileUpload: { files: File[], progressPercent: number } = {
      files: [],
      progressPercent: 0
   }
   uploadedFileInformation: File | null = null
   existingTOEFiles: string[] = []

   constructor(sp: ProposalsEditDetailsStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
      this.setProposalDetails()
   }

   get formHandler() {
      return formHandler(this.fields, {
         timing: { type: 'string', onValueChange: this.updateProposal },
         duringMonth: { type: 'date', onValueChange: this.updateProposal },
         signees: { type: 'string', onValueChange: this.updateProposal },
         signeesAmount: { type: 'number', onValueChange: this.updateProposal },
         selectedFilename: { type: 'string', onValueChange: this.newSelectedFile },
         dueDate: { type: 'string', onValueChange: this.updateProposal },
         dueDateDate: { type: 'date', onValueChange: this.updateProposal, useStartOfDay: true },
         objectivesAndValue: { type: 'any', onValueChange: this.updateProposal },
         expectations: { type: 'any', onValueChange: this.updateProposal }
      } satisfies FormHandlerConfig)
   }

   get proposal() {
      return this.sp.proposalsEditStore.proposal
   }

   get signeeOptions(): GapAutocompleteOption[] {
      const signeesData: { id: ProposalStep4SigneesEnum, label: string }[] = [
         { id: 'all-signees', label: 'All recipients' },
         { id: 'certain-number-of-signees', label: 'A certain number of recipients' },
      ]

      return signeesData.map(option => ({
         id: option.id,
         label: option.label,
      }))
   }

   /**
    * The count of recipients added to the proposal
    */
   get recipientCount() {
      return this.proposal.step1?.contacts?.length || 0 // should never get here without contacts
   }

   /**
    * The options for the signees amount dropdown
    */
   get signeeAmountOptions(): GapAutocompleteOption[] {
      return Array.from({ length: this.recipientCount }, (_, i) => ({
         id: (i + 1).toString(),
         label: (i + 1).toString()
      }))
   }

   /**
    * Helper text for the signees field
    */
   get signeesHelperText() {
      if (this.fields.signees === 'all-signees') {
         return 'This proposal will only be accepted when all recipients have signed.'
      }

      return this.fields.signees === 'certain-number-of-signees' && this.fields.signeesAmount > 0
         ? `This proposal will only be accepted when ${this.fields.signeesAmount} of the ${this.recipientCount} of recipients have signed.`
         : undefined
   }

   get objectivesAndValueOptions(): GapAutocompleteHierarchy | undefined {
      const allObjectives = proposalObjectivesAndValues
      if (!allObjectives) {
         return undefined
      }

      // Map categories to the GapAutocompleteHierarchy structure
      const categories = allObjectives.categories.map(category => ({
         id: category.id,
         label: category.name,
         items: category.items?.map(item => ({
            id: item.id,
            label: item.objective,
         })) || [],
         subcategories: category.subcategories?.map(subCategory => ({
            id: subCategory.id,
            label: subCategory.name,
            items: subCategory.items.map(item => ({
               id: item.id,
               label: item.objective,
            })),
         })) || [],
      }))

      return { categories }
   }

   get expectationOptions(): GapAutocompleteOption[] {
      return proposalExpectations.map(expectation => ({
         id: expectation.id,
         label: expectation.name
      })) || []
   }

   get timingOptions(): AutocompleteOption<ProposalStep4TimingEnum>[] {
      return [
         {
            id: "upon-acceptance",
            label: "Upon acceptance"
         },
         {
            id: "during-a-given-month",
            label: "During a given month"
         }
      ] as AutocompleteOption<ProposalStep4TimingEnum>[]
   }

   get dueDateOptions(): AutocompleteOption<ProposalStep4DueDateEnum>[] {
      return [
         {
            id: "1-week-from-now",
            label: "1 week from now (5 working days)"
         },
         {
            id: "2-weeks-from-now",
            label: "2 weeks from now (10 working days)"
         },
         {
            id: "specific-date",
            label: "On a specific date"
         }
      ] as AutocompleteOption<ProposalStep4DueDateEnum>[]
   }

   get selectFileOptions() {
      return this.existingTOEFiles.map(TOEFile => {
         const fileInfo = decodeFile(TOEFile)
         return {
            id: fileInfo.originalFile.name,
            label: fileInfo.file.name + ` (${fileInfo.created.toLocaleString()})`
         }
      })
   }

   get dueDateDate() {
      const inXWeeks = this.fields.dueDate === '1-week-from-now' ? 1 : 2
      const daysForward = inXWeeks * 5
      return DateUtils.addWorkingDays({ days: daysForward })
   }

   get dueDateHelperText() {
      if (this.fields.dueDate === 'specific-date') {
         return undefined
      }

      const inXWeeks = this.fields.dueDate === '1-week-from-now' ? 1 : 2
      return `${inXWeeks} week${inXWeeks > 1 ? 's' : ''} from now will be ${DateUtils.formatDate(this.dueDateDate, 'date-full-day')}`
   }

   /**
    * Shows the next month as a sample: 'Ex. Mar 2024' if the current date is feb 2024
    */
   get duringMonthPlaceHolder() {
      const date = new Date()
      const month = date.getMonth()
      const nextMonth = month + 1
      if (nextMonth === 12) {
         return `e.g. Jan ${date.getFullYear() + 1}`
      }

      return `e.g. ${monthNamesShort[nextMonth]} ${date.getFullYear()}`
   }

   /**
    * Custom value shown, as the normal datepicker value also shows the day
    */
   get datePickerValue() {
      const date = this.fields.duringMonth
      if (!date) {
         return null
      }
      const month = date.getMonth()
      return `${monthNamesShort[month]} ${date.getFullYear()}`
   }

   get stepIsDone() {
      if (!this.proposal.step4) {
         return false
      }

      // Timing must be set. If it is set to during-a-given-month, then the month should also be set
      if (!(this.proposal.step4.timing) || (this.proposal.step4.timing === 'during-a-given-month' && !this.fields.duringMonth)) {
         return false
      }

      // Signees must be set. If it is set to certain-number-of-signees, then the signees amount must also be set
      if (!(this.proposal.step4.signees) || this.proposal.step4.signees === 'certain-number-of-signees' && !this.fields.signeesAmount) {
         return false
      }

      // Due date must be set. If it is set to specific-date, then the dueDateDate must also be set
      if (!(this.proposal.step4.dueDate) || this.proposal.step4.dueDate === 'specific-date' && !this.fields.dueDateDate) {
         return false
      }

      // DueDateDate must be in the future
      if (this.fields.dueDateDate && this.fields.dueDateDate < DateUtils.yesterday()) {
         return false
      }

      // Everything should be selected. Terms of Engagements if not required
      return true
   }

   newSelectedFile = async () => {
      if (this.fields.selectedFilename && this.fields.selectedFilename !== '') {
         const fileInfo = decodeFile(this.fields.selectedFilename)
         this.uploadedFileInformation = fileInfo.originalFile
         this.fileUpload.files = [this.uploadedFileInformation]
      } else {
         this.fileUpload.files = []
      }
      await this.updateProposal()
   }

   fetch = async () => {
      await this.existingTOEFileLoader.executeLoading(async () => {
         this.existingTOEFiles = await apiClient.proposals.getAvailableTOEFiles.query()
      })
   }

   setProposalDetails = () => {
      if (this.proposal.step4?.objectivesAndValue) {
         this.fields.objectivesAndValue = this.proposal.step4.objectivesAndValue
      }
      if (this.proposal.step4?.expectations) {
         this.fields.expectations = this.proposal.step4.expectations
      }
      if (this.proposal.step4?.timing) {
         this.fields.timing = this.proposal.step4.timing
      }
      if (this.proposal.step4?.duringMonth) {
         this.fields.duringMonth = new Date(this.proposal.step4.duringMonth)
      }
      if (this.proposal.step4?.termsOfEngagementFilename) {
         const fileInfo = decodeFile(this.proposal.step4.termsOfEngagementFilename).originalFile
         this.uploadedFileInformation = fileInfo
         this.fileUpload.files = [this.uploadedFileInformation]
      }
      if (this.proposal.step4?.signees) {
         this.fields.signees = this.proposal.step4.signees
      }
      if (this.proposal.step4?.signeesAmount) {
         this.fields.signeesAmount = this.proposal.step4.signeesAmount
      }
      if (this.proposal.step4?.dueDate) {
         this.fields.dueDate = this.proposal.step4.dueDate
      }
      if (this.proposal.step4?.dueDateDate) {
         this.fields.dueDateDate = new Date(this.proposal.step4.dueDateDate)
      }

      // Seed the proposal with the field defaults when they haven't been persisted yet.
      // Otherwise the UI shows defaults for timing/signees/dueDate but stepIsDone reads
      // from proposal.step4, returning false until the user touches a field.
      if (!this.proposal.step4?.timing || !this.proposal.step4?.signees || !this.proposal.step4?.dueDate) {
         void this.updateProposal()
         return
      }

      this.sp.proposalsEditStore.stepperFullscreenStore.markStepStatus(this.stepIsDone)
   }

   removeFile = async () => {
      this.fileUpload.files = []
      this.uploadedFileInformation = null
   }

   getUploadUrl = async (file: File) => {
      this.uploadedFileInformation = file
      const { url } = await apiClient.proposals.getProposalsTOEUploadUrl.query({ orgId: notNull(this.sp.proposalsEditStore.org?.orgId), fileName: this.uploadedFileInformation.name })
      return url
   }

   newFile = async (newFiles: File[]) => {
      this.fields.selectedFilename = null
      this.uploadedFileInformation = newFiles[0]
      this.existingTOEFiles.push(this.uploadedFileInformation.name)
      await this.updateProposal()
   }

   updateProposal = async () => {
      if (!this.proposal.step4) {
         this.proposal.step4 = {}
      }

      // Objectives and value
      this.proposal.step4.objectivesAndValue = this.fields.objectivesAndValue

      // Expectations
      this.proposal.step4.expectations = this.fields.expectations

      // Timing
      this.proposal.step4.timing = this.fields.timing
      if (this.proposal.step4.timing === 'during-a-given-month' && this.fields.duringMonth) {
         const dateString = this.fields.duringMonth.toISOString()
         this.proposal.step4.duringMonth = DateUtils.formatTimezoneAgnosticISODate(dateString)
      } else {
         this.proposal.step4.duringMonth = undefined
      }

      // TOE file
      this.proposal.step4.termsOfEngagementFilename = this.uploadedFileInformation?.name

      // Signees
      this.proposal.step4.signees = this.fields.signees
      if (this.proposal.step4.signees === 'certain-number-of-signees' && this.fields.signeesAmount) {
         this.proposal.step4.signeesAmount = Number(this.fields.signeesAmount)
      } else {
         this.proposal.step4.signeesAmount = undefined
      }

      // Due date
      this.proposal.step4.dueDate = this.fields.dueDate
      if (this.proposal.step4.dueDate === 'specific-date' && this.fields.dueDateDate) {
         const dateString = this.fields.dueDateDate.toISOString()
         this.proposal.step4.dueDateDate = DateUtils.formatTimezoneAgnosticISODate(dateString)
      } else {
         this.proposal.step4.dueDateDate = this.dueDateDate.toISOString()
      }

      this.sp.proposalsEditStore.stepperFullscreenStore.markStepStatus(this.stepIsDone)

      await this.sp.proposalsEditStore.autoSave.updateProposal()
   }
}

export const { Provider: ProposalsEditDetailsStoreProvider, useProvidedStore: useProposalsEditDetailsStore } = createStoreContext(ProposalsEditDetailsStore)
